import Helmet from 'react-helmet';
import React from 'react';
import { withRouter, Link } from 'react-router';
import { connect } from 'react-redux';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import Autocomplete from 'react-autocomplete';
import _ from 'lodash';

import throttle from '../../util/throttle';
import { hasDashboardAccess } from '../../util/roles';
import { getLabelField } from '../../util/customFieldsUtils';

import FormattedMessage from '../../components/FormattedMessage';
import Header from '../../components/Header/Header';
import Loading from '../../components/Loading/Loading';
import ImageSize from '../../components/ImageSize/ImageSize';
import Checkbox from '../../components/Checkbox/Checkbox';
import Switch from '../../components/Switch/Switch';
import Confirm from '../../components/Confirm/Confirm';

import addIcon from '../../assets/img/add-circle.svg';
import saveIcon from '../../assets/img/save.svg';
import { isOnlyDashboard } from '../../util/dashboardUtils';

function idDecode(str) {
  if (!str) {
    return {};
  }
  const jsonStr = Buffer.from(str, 'base64').toString();
  return JSON.parse(jsonStr);
}

import {
  handleError,
  listAll,
  getTeams,
  getBoards,
  getLists,
  getAreas,
  create,
  updateModel,
  update,
  isLoading,
  selectDashboard,
  updateDashboardName
} from '../../components/DashboardSelector/DashboardSelectorActions';
import { setPageName, isLoading as appDataIsLoading } from '../App/AppActions';

import defaultBoardImg from '../../assets/images/board-default-icon.svg';
import personalBoardImg from '../../assets/images/board-person-icon.svg';
import deleteIcon from '../../assets/img/delete.svg';
import spinerArrow from '../../assets/images/spiner-arrow.svg';

// Import Style
import './createDashboard.scss';

// a little function to help us with reordering the result
const reorder = (list, startIndex, endIndex) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);

  return result;
};

/**
 * Moves an item from one list to another list.
 */
const move = (source, destination, droppableSource, droppableDestination) => {
  const sourceClone = Array.from(source);
  const destClone = Array.from(destination);
  const [removed] = sourceClone.splice(droppableSource.index, 1);

  destClone.splice(droppableDestination.index, 0, removed);

  const result = {};
  result[droppableSource.droppableId] = sourceClone;
  result[droppableDestination.droppableId] = destClone;

  return result;
};

const getItemStyle = (isDragging, draggableStyle) => ({
  position: 'relative',
  userSelect: 'none',
  minHeight: '40px',
  padding: '8px 40px 8px 8px' ,
  borderRadius: '8px',
  background: '#fff',
  color: '#001A33',
  fontSize: '14px',
  fontWeight: '500',
  lineHeight: '24px',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  ...draggableStyle
});

const getListStyle = isDraggingOver => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  maxHeight: '100%',
  minHeight: '60px',
  padding: '16px',
  borderRadius: '10px',
  background: isDraggingOver ? '#f2f9fb' : '#EFF6FB',
  overflow: 'hidden auto',
});

export class CreateIntegrationDashboard extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      name: '',
      label: '',
      boards: [],
      board: {},
      model: {},
      lists: [],
      todo: [],
      wip: [],
      done: [],
      blockedFlags: [],
      selectedBlockedFlag: '',
      blockedSearchVal: '',
      searchVal: '',
      teamSearchVal: '',
      fieldSearchVal: '',
      areaSearchVal: '',
      selectedFields: [],
      selectedArea: '',
      akbBoardId: '',
      wipeOut: false,
      isUpdate: false,
      includeChildAreas: true
    };

    this.getBoards = throttle(props.getBoards, 500, this);

    props.setDataLoading(false);
    props.dispatch(setPageName('Create dashboard'));
  }

  componentDidMount() {
    document.addEventListener('click', this.handleOutsideClick, false);

    if (this.props.route.path === 'create' && (!this.props.boards.length || this.props.outside)) {
      if (this.props.app !== 'azure') {
        this.getBoards('');
      } else if (!this.props.teams || !this.props.teams.length) {
        this.props.getTeams();
      }
    }

    if (this.props.route.path !== 'create') {
      this.props.dispatch(setPageName('Update dashboard'));
      if (this.props.app == 'azure' && this.props.dashboardModel && (!this.props.areas || !this.props.areas.length)) {
        this.props.getAreas(this.props.dashboardModel.board.organizationId);
      }

      this.setState({ isUpdate: true });

      if (this.props.dashboardModel) {
        this.props.getLists(this.props.dashboardModel.boardId, this.getOrgId(), this.props.params.id);
        if (this.props.app == 'azure') {
          this.setState({
            areaSearchVal: this.props.dashboardModel.selectedArea
          });
        }
      } else {
        this.props.listAll();
      }
    } else {
      this.props.resetModel();
      this.input && this.input.focus();
    }
  }

  componentWillUnmount() {
    document.removeEventListener('click', this.handleOutsideClick, false);
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.props.route.path === 'create' && prevProps.user != this.props.user) {
      if (
        this.props.user.allowedDashboards > 0 &&
        this.props.user.dashboards > 0 &&
        this.props.user.dashboards >= this.props.user.allowedDashboards
      ) {
        this.props.router.replace(`/limitreached${ this.props.router.location.search || '' }`);
        return;
      }
    }
    if (this.props.created === true && prevProps.created != this.props.created) {
      this.props.loadingStart();
      this.props.listAll().then(() => {
        this.props.loadingDone();
        this.props.setDataLoading(true);

        const last = _(this.props.dashboards)
          .orderBy('createdAt')
          .last();
        const dashboardId = this.props.params.id ? this.props.params.id : last._id;
        this.props.router.push(`/dashboard/${dashboardId}/process`);
      });
    }

    if (prevProps.dashboards != this.props.dashboards) {
      const dashboard = this.props.dashboards.find(d => d._id == this.props.params.id);
      this.props.updateModel(dashboard);
    }

    if (this.props.dashboardModel && !prevProps.dashboardModel) {
      this.props.getLists(this.props.dashboardModel.boardId, this.getOrgId());
      if (this.props.app == 'azure') {
        if (!this.props.areas || !this.props.areas.lenght) {
          this.props.getAreas(this.props.dashboardModel.board.organizationId);
        }
        this.setState({
          areaSearchVal: this.props.dashboardModel.selectedArea
        });
      }
    }

    if (prevProps.boards != this.props.boards && this.props.app === 'jira') {
      this.input && this.input.click();
    }

    if (this.props.outside && prevProps.boards != this.props.boards && this.props.boards.length) {
      if (this.props.route.path === 'create' && this.props.app === 'jira' && this.props.plugin.projectId) {
        this.input && this.input.click();
      }
      else if (this.props.plugin.boardId) {
        let board = this.props.boards.find(d => d._id == this.props.plugin.boardId);
        const akbBoardId = this.props.plugin.aid;
  
        if (!board) {
          let boardId = this.props.plugin.boardId;
          if (!boardId && this.props.dashboardModel) {
            boardId = this.props.dashboardModel.boardId;
            board = this.props.boards.find(d => d._id == boardId);
          }
  
          if (!board) {
            board = {
              _id: boardId,
              name: ''
            };
          }
        }
  
        this.handleBoardChange(board.name, board, akbBoardId);
      }
    }

    if (prevProps.fields !== this.props.fields) {
      let selectedFields = this.state.selectedFields;
      if (!this.state.isUpdate) {
        if (this.props.app === 'trello') {
          selectedFields = _(this.props.fields)
            .filter(f => {
              return f.extId !== 'members';
            })
            .map(f => f.extId)
            .value();
        } else if (this.props.app === 'jira') {
          selectedFields = _(this.props.fields)
            .filter(f => {
              // Set default custom fields selection
              return _.indexOf(['assignees'], f.extId) === -1;
            })
            .map(f => f.extId)
            .value();
        } else {
          selectedFields = [getLabelField(this.props.app)];
        }
      } else {
        selectedFields = this.getSelectedFields(this.props.fields, this.props.dashboardModel.selectedFields);
      }

      let blockedFlags = this.state.blockedFlags;
      let selectedBlockedFlag = this.state.selectedBlockedFlag;
      let blockedSearchVal = this.state.blockedSearchVal;
      const { business } = this.props.app === 'jira' && idDecode(this.state.board._id);
      if (this.props.app === 'azure' || this.props.app === 'trello' || business) {
        blockedFlags = business
          ? _.filter(this.props.fields, f => f.extId.indexOf('customfield_') > -1 && f.type == 'array')
          : _.filter(this.props.fields, { type: this.props.app === 'azure' ? 'boolean' : 'checkbox' });
        if (this.state.isUpdate) {
          selectedBlockedFlag = this.props.dashboardModel.selectedBlockedFlag;
          const blockedField = _.find(blockedFlags, { extId: selectedBlockedFlag });
          blockedSearchVal = (blockedField && blockedField.name) || '';
        }
        blockedFlags.unshift({
          extId: 'no-option',
          name: '(no option)'
        });
      } else {
        selectedBlockedFlag = '';
        blockedSearchVal = '';
        blockedFlags = [];
      }

      this.setState({ selectedFields, blockedFlags, selectedBlockedFlag, blockedSearchVal });
    }

    if (prevState.selectedFields !== this.state.selectedFields) {
      this.setState({
        fieldsValue:
          this.state.selectedFields.length > 0
            ? `${this.state.selectedFields.length} field${this.state.selectedFields.length !== 1 ? 's' : ''} selected`
            : '',
        fieldSearchVal:
          this.state.selectedFields.length > 0
            ? `${this.state.selectedFields.length} field${this.state.selectedFields.length !== 1 ? 's' : ''} selected`
            : ''
      });
    }
  }

  componentWillReceiveProps(newProps) {
    if (newProps.lists && newProps.lists != this.props.lists) {
      this.updateDashboardModelState(newProps);
    }
  }

  updateDashboardModelState(newProps) {
    if (this.props.dashboardModel && !this.modelCreated) {
      this.setState({
        name: newProps.dashboardModel.name,
        label: newProps.dashboardModel.label,
        boardId: newProps.dashboardModel.boardId,
        board: newProps.dashboardModel.board,
        todo: this.getListsByIds(newProps.lists, newProps.dashboardModel.todo),
        wip: this.getListsByIds(newProps.lists, newProps.dashboardModel.wip),
        done: this.getListsByIds(newProps.lists, newProps.dashboardModel.done),
        selectedBlockedFlag: newProps.dashboardModel.selectedBlockedFlag,
        selectedArea: newProps.dashboardModel.selectedArea,
        selectedFields: this.getSelectedFields(newProps.fields, newProps.dashboardModel.selectedFields),
        includeChildAreas: newProps.dashboardModel.includeChildAreas
      });
      this.modelCreated = true;
    } else {
      if (
        newProps.mapping &&
        newProps.mapping.todo.length &&
        newProps.mapping.wip.length &&
        newProps.mapping.done.length
      ) {
        this.setState({
          todo: this.getListsByIds(newProps.lists, newProps.mapping.todo),
          wip: this.getListsByIds(newProps.lists, newProps.mapping.wip),
          done: this.getListsByIds(newProps.lists, newProps.mapping.done)
        });
      } else {
        this.setState({
          lists: newProps.lists
        });
      }
    }
  }

  getOrgId() {
    if (this.props.dashboardModel && this.props.dashboardModel.organization) {
      return this.props.dashboardModel.organization._id;
    }

    return '';
  }

  getFlags() {
    if (this.props.fields) {
      return _.filter(this.props.fields, { type: 'boolean' });
    }
    return [];
  }

  getListsByIds(list, ids) {
    const newList = _(list)
      .remove(obj => ids.includes(obj._id))
      .sortBy(l => {
        return ids.indexOf(l._id);
      })
      .value();
    this.setState({
      lists: list
    });
    return newList;
  }

  createDashboardModel() {
    return {
      name: this.state.name,
      label: this.state.label,
      boardId: this.state.boardId,
      board: this.state.board,
      todo: this.state.todo.map(i => i._id),
      wip: this.state.wip.map(i => i._id),
      done: this.state.done.map(i => i._id),
      selectedBlockedFlag: this.state.selectedBlockedFlag,
      selectedFields: this.state.selectedFields,
      selectedArea: this.state.selectedArea,
      akbBoardId: this.state.akbBoardId,
      wipeOut: this.state.wipeOut,
      includeChildAreas: this.state.includeChildAreas
    };
  }

  validate() {
    if (this.props.saving) {
      return false;
    }

    if (this.props.app == 'azure') {
      if (!this.state.name) {
        this.props.setError({
          data: {
            dashboard: {
              msg: 'error.dashboard.name'
            }
          }
        });
        return false;
      }
      if (!this.state.selectedArea) {
        this.props.setError({
          data: {
            dashboard: {
              msg: 'error.area.notfound'
            }
          }
        });
        return false;
      }
    }

    return true;
  }

  handleCreate = () => {
    if (!this.validate()) {
      return false;
    }

    const model = this.createDashboardModel();
    model.anonymize = this.state.anonymize;

    this.props.create(model);
  };

  handleUpdate = () => {
    if (!this.validate()) {
      return false;
    }

    const model = this.createDashboardModel();
    this.props.update(this.props.dashboardModel._id, model);
  };

  handleTeamChange = (autocompleteVal, team) => {
    if (team) {
      this.setState({
        boardId: '',
        lists: [],
        todo: [],
        wip: [],
        done: [],
        selectedFields: [],
        teamSearchVal: autocompleteVal,
        searchVal: '',
        fieldSearchVal: '',
        areaSearchVal: ''
      });
      this.props.getBoards('', team.projectId, team.id);
    }
  };

  clearBoard = () => {
    this.props.resetModel();
    this.setState({
      boardId: null,
      board: null,
      wipeOut: false,
      akbBoardId: '',
      lists: [],
      todo: [],
      wip: [],
      done: [],
      selectedFields: [],
      fieldSearchVal: '',
      areaSearchVal: '',
      anonymize: undefined
    });
  };

  handleBoardChange = (autocompleteVal, board, akbBoardId) => {
    if (board.projectsSkip || board.boardsSkip) {
      const { boardsInfo } = this.props;
      this.props.getBoards(
        this.state.searchVal,
        undefined,
        undefined,
        board.projectsSkip || boardsInfo.projectsTotal,
        board.boardsSkip || boardsInfo.boardsTotal
      );
      return;
    }
    this.props.resetModel();
    this.setState({
      searchVal: autocompleteVal,
      boardId: board._id,
      board,
      akbBoardId: akbBoardId,
      wipeOut: false,
      lists: [],
      todo: [],
      wip: [],
      done: [],
      selectedFields: [],
      fieldSearchVal: '',
      areaSearchVal: '',
      anonymize: board.anonymize
    });
    this.props.getLists(board._id, board.organizationId);

    if (this.props.app === 'azure') {
      this.props.getAreas(board.organizationId);
    }
  };

  selectArea = area => {
    this.setState({
      selectedArea: area.name,
      areaSearchVal: area.name
    });
  };

  getSelectedFields(allFields, selectedFields) {
    return _.filter(selectedFields, cf => {
      return _.findIndex(allFields, { extId: cf }) > -1;
    });
  }

  getValidationState() {
    return null;
  }

  id2List = {
    lists: 'lists',
    todo: 'todo',
    wip: 'wip',
    done: 'done'
  };

  getList = id => this.state[this.id2List[id]];

  onDragEnd = result => {
    const { source, destination } = result;

    // dropped outside the list
    if (!destination) {
      return;
    }

    if (source.droppableId === destination.droppableId) {
      const items = reorder(this.getList(source.droppableId), source.index, destination.index);
      this.setState({ [source.droppableId]: items });
    } else {
      const resultArrs = move(
        this.getList(source.droppableId),
        this.getList(destination.droppableId),
        source,
        destination
      );

      this.setState({
        lists: resultArrs.lists || this.state.lists,
        todo: resultArrs.todo || this.state.todo,
        wip: resultArrs.wip || this.state.wip,
        done: resultArrs.done || this.state.done
      });
    }
  };

  onSearch = (event, value) => {
    if (this.props.app === 'jira' || this.props.app === 'asana') {
      this.clearBoard();
      this.getBoards(value);
    }

    const searchProp = this.props.app === 'azure' ? 'teamSearchVal' : 'searchVal';
    this.setState({ [searchProp]: value });
  };

  matchBoardToTerm = (board, value) => {
    if (this.props.app === 'jira' || this.props.app === 'asana') {
      return true;
    }

    return (
      board.name.toLowerCase().indexOf(value.toLowerCase()) !== -1 ||
      (board.organization &&
        board.organization.displayName &&
        board.organization.displayName.toLowerCase().indexOf(value.toLowerCase()) !== -1)
    );
  };

  matchTeamToTerm = (team, value) => {
    return (
      team.name.toLowerCase().indexOf(value.toLowerCase()) !== -1 ||
      (team.projectName && team.projectName.toLowerCase().indexOf(value.toLowerCase()) !== -1)
    );
  };

  fieldIsSelected = id => {
    return this.state.selectedFields.indexOf(id) > -1;
  };

  selectField = field => {
    const selected = this.state.selectedFields;
    if (!this.fieldIsSelected(field.extId)) {
      selected.push(field.extId);
    } else {
      const index = selected.indexOf(field.extId);
      selected.splice(index, 1);
    }

    this.setState(
      {
        selectedFields: selected,
        fieldsValue: selected.length > 0 ? `${selected.length} field${selected.length !== 1 ? 's' : ''} selected` : ''
      },
      () => this.handleFieldsAutocompleteVisibility(true)
    );
  };

  handleAutocompleteVisibility = isOpen => {
    if (isOpen) {
      this.setState({
        tempVal: '',
        searchVal: this.state.tempVal
      });
    } else {
      this.setState({
        tempVal: this.state.searchVal,
        searchVal: ''
      });
    }
  };

  handleTeamsAutocompleteVisibility = isOpen => {
    if (isOpen) {
      this.setState({
        tempVal: this.state.teamSearchVal,
        teamSearchVal: ''
      });
    } else {
      this.setState({
        tempVal: '',
        teamSearchVal: this.state.tempVal
      });
    }
  };

  handleBlockedAutocompleteVisibility = isOpen => {
    if (isOpen) {
      this.setState({
        tempVal: this.state.blockedSearchVal,
        blockedSearchVal: ''
      });
    } else {
      this.setState({
        tempVal: '',
        blockedSearchVal: this.state.tempVal
      });
    }
  };

  selectBlockedFlag = field => {
    const noOption = field.extId == 'no-option';
    this.setState({
      selectedBlockedFlag: noOption ? '' : field.extId,
      blockedSearchVal: noOption ? '' : field.name
    });
  };

  handleFieldsAutocompleteVisibility = isOpen => {
    this.setState({
      fieldSearchVal: isOpen ? '' : this.state.fieldsValue,
      fieldsAutocompleteShouldOpen: isOpen
    });
  };

  handleAreasAutocompleteVisibility = isOpen => {
    if (isOpen) {
      this.setState({
        tempVal: this.state.areaSearchVal,
        areaSearchVal: ''
      });
    } else {
      this.setState({
        tempVal: '',
        areaSearchVal: this.state.tempVal
      });
    }
  };

  handleOutsideClick = e => {
    const cfa = document.getElementById('fields-autocomplete');
    if (cfa && cfa.contains(e.target)) {
      return false;
    } else if (cfa) {
      const menu = cfa.querySelector('#menu');
      const className = e.target.className && typeof e.target.className === 'string' ? `.${e.target.className}` : '';
      if (menu && menu.querySelector(`${e.target.localName}${className}`)) {
        return false;
      }
    }

    this.handleFieldsAutocompleteVisibility(false);
  };

  handleCheckboxChange = e => {
    this.setState({ wipeOut: e.checked });
  };

  handleDashboardNameChange = (e, isAzure) => {
    if (!e.target.value) {
      this.props.setError({
        data: {
          dashboard: {
            msg: 'error.dashboard.name'
          }
        }
      });

      return false;
    }

    const getName = () => {
      if (!this.props.dashboardModel.label) {
        return this.props.dashboardModel.name;
      }
      return this.props.dashboardModel[isAzure ? 'name' : 'label'] || this.props.dashboardModel.name;
    };

    if (!!this.props.dashboardModel && e.target.value !== getName()) {
      this.props.updateDashboardName(this.props.params.id, e.target.value);
    }
  };

  renderDashboardName = isAzure => {
    return (
      <div className='dashboard_name'>
        <p className='title_label fw_sb'>Dashboard name</p>
        <input
          className='form-control'
          placeholder="Dashboard name"
          value={this.state[isAzure ? 'name' : 'label'] || this.state['name']}
          onChange={e => this.setState({ [isAzure ? 'name' : 'label']: e.target.value })}
          onBlur={e => this.handleDashboardNameChange(e, isAzure)}
        ></input>
      </div>
    );
  };

  renderDroppable = (name, title, text, list, className) => {
    return [
      <div key={name} className={className}>
        <h4 className='title_label'>{title} ({list.length})</h4>
        <Droppable droppableId={name}>
          {(provided, snapshot) => (
            <div ref={provided.innerRef} style={getListStyle(snapshot.isDraggingOver)} {...provided.droppableProps}>
              {list.map((item, index) => (
                <Draggable key={item._id} draggableId={item._id} index={index}>
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                      style={getItemStyle(snapshot.isDragging, provided.draggableProps.style)}
                    >
                      {item.name}
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
              <div className='drag_drop_box'>{text}</div>
              {(list.length == 0) &&  
                <React.Fragment>
                  {name == 'lists' && 
                    <div className="well_done_box">
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="25" viewBox="0 0 24 25" fill="none">
                        <path fill-rule="evenodd" clip-rule="evenodd" d="M2.00574 12.873C2.00574 7.35305 6.48574 2.87305 12.0057 2.87305C17.5257 2.87305 22.0057 7.35305 22.0057 12.873C22.0057 18.393 17.5257 22.873 12.0057 22.873C6.48574 22.873 2.00574 18.393 2.00574 12.873ZM5.70574 13.573L9.29574 17.163C9.68574 17.553 10.3257 17.553 10.7057 17.163L18.2957 9.57305C18.6857 9.18305 18.6857 8.55305 18.2957 8.16305C17.9057 7.77305 17.2757 7.77305 16.8857 8.16305L10.0057 15.043L7.11574 12.163C6.72574 11.773 6.09574 11.773 5.70574 12.163C5.51848 12.3499 5.41325 12.6035 5.41325 12.868C5.41325 13.1326 5.51848 13.3862 5.70574 13.573Z" fill="#0E391F"/>
                      </svg>
                      <span>Well done! All your statuses are mapped. Your analytics are now set up for accurate insights.</span>
                    </div>
                  }
                </React.Fragment>
              }
              
              {name == 'lists' && list.length > 0 && 
                <div className="time_spent_box">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="25" viewBox="0 0 24 25" fill="none">
                    <path fill-rule="evenodd" clip-rule="evenodd" d="M19.5276 21.2795C21.0676 21.2795 22.0276 19.6095 21.2576 18.2795L13.7276 5.26947C12.9576 3.93947 11.0376 3.93947 10.2676 5.26947L2.73756 18.2795C1.96756 19.6095 2.92756 21.2795 4.46756 21.2795H19.5276ZM11.9976 14.2795C11.4476 14.2795 10.9976 13.8295 10.9976 13.2795V11.2795C10.9976 10.7295 11.4476 10.2795 11.9976 10.2795C12.5476 10.2795 12.9976 10.7295 12.9976 11.2795V13.2795C12.9976 13.8295 12.5476 14.2795 11.9976 14.2795ZM10.9976 16.2795V18.2795H12.9976V16.2795H10.9976Z" fill="#B37100"/>
                  </svg>
                  <span>Time spent in unmapped statuses won’t be tracked in your analytics. Only leave statuses unmapped if they’re no longer in use.</span>
                </div>
              }
            </div>
          )}
        </Droppable>
      </div>
    ];
  };

  renderLists = () => {
    return (
      <DragDropContext onDragEnd={this.onDragEnd}>
        {this.renderDroppable(
          'lists',
          <span>
            Unmapped <FormattedMessage id={`${this.props.app}.list`} mode="capitalize" plural="true" />
          </span>,
          <span>Drag & drop <FormattedMessage id={`${this.props.app}.list`} plural="true" /> you wish to unmap here</span>,
          this.state.lists,
          'trello_select_process lists'
        )}
        {this.renderDroppable(
          'todo',
          'To Do',
          <span>
            Drag & drop your <FormattedMessage id={`${this.props.app}.list`} plural="true" /> with tasks that haven't started yet here
          </span>,
          this.state.todo,
          'trello_list_process todo'
        )}
        {this.renderDroppable(
          'wip',
          'Doing',
          <span>
            Drag & drop your <FormattedMessage id={`${this.props.app}.list`} plural="true" /> with tasks in progress here
          </span>,
          this.state.wip,
          'trello_list_process doing'
        )}
        {this.renderDroppable(
          'done',
          'Done',
          <span>
            Drag & drop your <FormattedMessage id={`${this.props.app}.list`} plural="true" /> with completed tasks here
          </span>,
          this.state.done,
          'trello_list_process done'
        )}
      </DragDropContext>
    );
  };

  renderDefaultDropdowns = () => {
    const { searchVal, fieldSearchVal, blockedSearchVal, blockedFlags, boardId, isUpdate } = this.state;
    const { app, appName, dashboardModel, boards, boardsInfo, outside, issue, plugin } = this.props;

    const showedBoards = _.filter(boards, b => !b.canLoadMore);
    const boardsSkip =
      boardsInfo.boardsSkip + boardsInfo.boardsLoaded < boardsInfo.boardsTotal
        ? boardsInfo.boardsSkip + boardsInfo.boardsLoaded
        : 0;
    const projectsSkip =
      boardsInfo.projectsSkip + boardsInfo.projectsLoaded < boardsInfo.projectsTotal
        ? boardsInfo.projectsSkip + boardsInfo.projectsLoaded
        : 0;
    if (boardsSkip || projectsSkip) {
      showedBoards.push({ boardsSkip, projectsSkip });
    }
    
    const isOutside = outside && !!plugin && !!plugin.boardId;
    const isJiraPlugin = plugin && plugin.app === 'jira';
    const akbBoardId = outside && !!plugin && plugin.aid;
    const showDropDown = !isOutside || isJiraPlugin && !akbBoardId; 
    if (isUpdate && issue) {
      return
    }

    return (
      <div className='head_control'>
        {!!dashboardModel && this.renderDashboardName()}
        {showDropDown && !dashboardModel && (
          <div className='autocomplete'>
            <p className='title_label fw_sb'>
              Select <FormattedMessage id={`${app}.board`} prefix={app} mode="capitalize" />
            </p>
            <Autocomplete
              ref={el => (this.input = el)}
              getItemValue={item => item.name}
              items={showedBoards}
              renderInput={props => <input placeholder="Search" {...props} />}
              renderMenu={(items, value, style) => {
                return items.length ? (
                  <div style={{ ...style, ...this.menuStyle }}><div className="scroll" children={items} /></div>
                ) : !value ? (
                  <div className='no_boards_found'>
                    <span>
                      No <FormattedMessage id={`${app}.board`} plural="true" /> found. Please create a{' '}
                      <FormattedMessage id={`${app}.board`} /> in {appName} in order to continue
                    </span>
                  </div>
                ) : (
                  <div className='no_boards_found'>
                    <span>
                      No <FormattedMessage id={`${app}.board`} plural="true" /> found
                    </span>
                  </div>
                );
              }}
              renderItem={(board, isHighlighted) => {
                if (board.projectsSkip || board.boardsSkip) {
                  return (
                    <div className='dropdown_list'>
                      <div className='load_more'>Load more ...</div>
                    </div>
                  );
                } else {
                  return (
                    <div
                      key={board._id}
                      className='dropdown_list'
                      style={{ background: isHighlighted ? '#E6F1F7' : '' }}
                    >
                      <span className='logo'>
                        {board.organizationId && !board.organization.logo ? (
                          <ImageSize src={defaultBoardImg} alt="#" />
                        ) : !board.organizationId ? (
                          <ImageSize src={personalBoardImg} alt="#" />
                        ) : (
                          <ImageSize
                            src={board.organization.logo}
                            alt="#"
                            onError={({ currentTarget }) => {
                              currentTarget.onerror = null; // prevents looping
                              currentTarget.src = defaultBoardImg;
                            }}
                          />
                        )}
                      </span>
                      <div className='dashboard_name'>
                        <span className='company'>
                          {board.team
                            ? board.team.name
                            : board.organizationId
                            ? board.organization.displayName
                            : 'Personal'}
                        </span>
                        <span className='project'>{board.name}</span>
                      </div>
                    </div>
                  );
                }
              }}
              value={searchVal}
              onSelect={(value, item) => this.handleBoardChange(value, item, akbBoardId)}
              onChange={this.onSearch}
              shouldItemRender={this.matchBoardToTerm}
              onMenuVisibilityChange={this.handleAutocompleteVisibility}
            />
          </div>
        )}
       
        {!!boardId && blockedFlags && !!blockedFlags.length && (
          <div className='blocked_flag autocomplete'>
            { blockedFlags.length === 1 ? (
              <OverlayTrigger
                overlay={
                  <Tooltip id="blockedFlagTooltip" className="tooltip_white full_width" style={{ minWidth: 'calc((100vw - 140px) / 4 - 15px)' }}>
                    You can create a custom field of type "Checkbox" in your board to use it as a blocked flag
                  </Tooltip>
                }
                delay={{ show: 10, hide: 1000 }}
                placement="top"
                trigger={['hover', 'focus', 'click']}
              >
                <p className="title_label fw_sb">
                  Select blocked flag
                  <span className="more_info" />
                </p>
              </OverlayTrigger>
            ) : (
              <p className="title_label fw_sb">Select blocked flag</p>
            )}
            <Autocomplete
              getItemValue={item => item.name}
              items={blockedFlags}
              inputProps={{ disabled: blockedFlags.length === 1 }}
              renderInput={props => <input placeholder="Search" {...props} />}
              renderMenu={(items, value, style) => {
                return items.length ? (
                  <div style={{ ...style, ...this.menuStyle }} children={items} />
                ) : (
                  <div className='no_boards_found'>
                    <span>No flags found</span>
                  </div>
                );
              }}
              renderItem={(blocked, isHighlighted) => (
                <div
                  key={blocked.extId}
                  className='dropdown_list'
                  style={{ background: isHighlighted ? '#E6F1F7' : '' }}
                >
                  <div className='dashboard_name no-img'>
                    <span className='project'>{blocked.name}</span>
                  </div>
                </div>
              )}
              value={blockedSearchVal}
              onChange={(ev, value) => this.setState({ blockedSearchVal: value })}
              onSelect={(value, blocked) => this.selectBlockedFlag(blocked)}
              shouldItemRender={(blocked, value) => {
                return blocked.name.toLowerCase().indexOf(value.toLowerCase()) !== -1;
              }}
              onMenuVisibilityChange={this.handleBlockedAutocompleteVisibility}
            />
          </div>
        )}

        {!!boardId && this.props.fields && !!this.props.fields.length && (
          <div className='enable_custom_fields autocomplete'>
            <p className='title_label fw_sb'>Enable custom fields</p>
            <Autocomplete
              wrapperProps={{ id: 'fields-autocomplete' }}
              getItemValue={item => item.name}
              items={this.props.fields}
              renderInput={props => <input placeholder="Search" {...props} />}
              renderMenu={(items, value, style) => {
                return items.length ? (
                  <div id="menu" style={{ ...style, ...this.menuStyle }} children={items} />
                ) : (
                  <div className='no_boards_found'>
                    <span>No custom fields found</span>
                  </div>
                );
              }}
              renderItem={(field, isHighlighted) => (
                <div
                  key={field.extId}
                  className='dropdown_list'
                  style={{ background: isHighlighted ? '#E6F1F7' : '' }}
                >
                  <Checkbox
                    name={field.extId}
                    id={field.extId}
                    label={field.name}
                    checked={this.fieldIsSelected(field.extId)}
                    onChange={e => this.selectField(field)}
                  />
                </div>
              )}
              value={fieldSearchVal}
              onChange={(ev, value) => this.setState({ fieldSearchVal: value })}
              onSelect={(value, field) => this.selectField(field)}
              shouldItemRender={(field, value) => {
                return field.name.toLowerCase().indexOf(value.toLowerCase()) !== -1;
              }}
              open={this.state.fieldsAutocompleteShouldOpen}
              onMenuVisibilityChange={this.handleFieldsAutocompleteVisibility}
            />
          </div>
        )}
      </div>
    );
  };

  renderAzureDropdowns = () => {
    const { searchVal, teamSearchVal, fieldSearchVal, blockedSearchVal, blockedFlags, boardId, isUpdate } = this.state;
    const { app, appName, dashboardModel, teams, boards, outside, issue } = this.props;

    const mainView = !outside || (outside && outside.app === 'jira' && outside.noBoard)

    if (isUpdate && issue) {
      return;
    }

    return [
      <div key="1" className='head_control'>
        <div className='dashboard_name'>{this.renderDashboardName(true)}</div>
        {mainView && (
          <div className={`autocomplete ${!!isUpdate && 'disabled' }`}>
            <p className='title_label fw_sb'>Select Azure team</p>
            <Autocomplete
              ref={el => (this.input = el)}
              getItemValue={item => item.projectName ? `${item.projectName} - ${item.name}`: item.name}
              items={teams || []}
              renderInput={props => <input placeholder="Search" {...props} />}
              renderMenu={(items, value, style) => {
                return items.length ? (
                  <div style={{ ...style, ...this.menuStyle }} children={items} />
                ) : !value ? (
                  <div className='no_boards_found'>
                    <span>No team found. Please create a team in {appName} in order to continue</span>
                  </div>
                ) : (
                  <div className='no_boards_found'>
                    <span>No team found</span>
                  </div>
                );
              }}
              renderItem={(team, isHighlighted) => (
                <div
                  key={team.id}
                  className='dropdown_list'
                  style={{ background: isHighlighted ? '#E6F1F7' : '' }}
                >
                  <span className='logo'>
                    <ImageSize src={defaultBoardImg} alt="#" />
                  </span>
                  <div className='dashboard_name'>                    
                    { team.projectName ?
                      ( <span className='project'>
                          {team.projectName} - {team.name}
                        </span>)
                      : ( <span className='project'>{team.name}</span>)
                    }                      
                  </div>
                </div>
              )}
              inputProps={{ disabled: !!isUpdate }}
              value={!!dashboardModel ? dashboardModel.organization.name : teamSearchVal}
              onSelect={(value, item) => this.handleTeamChange(value, item)}
              onChange={this.onSearch}
              shouldItemRender={this.matchTeamToTerm}
              onMenuVisibilityChange={this.handleTeamsAutocompleteVisibility}
            />
          </div>
        )}
        {mainView && (
          <div className={`autocomplete ${!!isUpdate ? 'disabled' : '' }`}>
            <p className='title_label fw_sb'>
              Select <FormattedMessage id={`${app}.board`} prefix={app} mode="capitalize" />
            </p>
            <Autocomplete
              disabled={true}
              getItemValue={item => item.name}
              items={boards}
              renderInput={props => <input placeholder="Search" {...props} />}
              renderMenu={(items, value, style) => {
                return items.length ? (
                  <div style={{ ...style, ...this.menuStyle }} children={items} />
                ) : !value ? (
                  <div className='no_boards_found'>
                    <span>
                      No <FormattedMessage id={`${app}.board`} plural="true" /> found. Please create a{' '}
                      <FormattedMessage id={`${app}.board`} /> in {appName} in order to continue
                    </span>
                  </div>
                ) : (
                  <div className='no_boards_found'>
                    <span>
                      No <FormattedMessage id={`${app}.board`} plural="true" /> found
                    </span>
                  </div>
                );
              }}
              renderItem={(board, isHighlighted) => (
                <div
                  key={board._id}
                  className='dropdown_list'
                  style={{ background: isHighlighted ? '#E6F1F7' : '' }}
                >
                  <span className='logo'>
                    <ImageSize src={defaultBoardImg} alt="#" />
                  </span>
                  <div className='dashboard_name'>
                    <span className='project'>{board.name}</span>
                  </div>
                </div>
              )}
              inputProps={{ disabled: !!isUpdate }}
              value={!!dashboardModel ? dashboardModel.board.name : searchVal}
              onSelect={(value, item) => this.handleBoardChange(value, item)}
              shouldItemRender={this.matchBoardToTerm}
              onMenuVisibilityChange={this.handleAutocompleteVisibility}
            />
          </div>
        )}
        {mainView && (
          <div className='enable_custom_fields autocomplete'>
            <p className='title_label fw_sb'>Select area path</p>
            <Autocomplete
              disabled={true}
              getItemValue={item => item.name}
              items={this.props.areas}
              renderInput={props => <input placeholder="Search" {...props} />}
              renderMenu={(items, value, style) => {
                return items.length ? (
                  <div style={{ ...style, ...this.menuStyle }} children={items} />
                ) : (
                  <div className='no_boards_found'>
                    <span>No Area Path found</span>
                  </div>
                );
              }}
              renderItem={(area, isHighlighted) => (
                <div
                  key={area.id}
                  className='dropdown_list'
                  style={{ background: isHighlighted ? '#E6F1F7' : '' }}
                >
                  <div className='dashboard_name no-img'>
                    <span className='project'>{area.name}</span>
                  </div>
                </div>
              )}
              value={this.state.areaSearchVal}
              onChange={(ev, value) => this.setState({ areaSearchVal: value })}
              onSelect={(value, area) => this.selectArea(area)}
              shouldItemRender={(area, value) => {
                return area.name.toLowerCase().indexOf(value.toLowerCase()) !== -1;
              }}
              onMenuVisibilityChange={this.handleAreasAutocompleteVisibility}
            />

            
            <Checkbox
              className='mt-4'
              name="includeChildAreas"
              id="includeChildAreas"
              label="Include child area paths"
              checked={this.state.includeChildAreas}
              onChange={e => this.setState({ includeChildAreas: e.checked })}
            />
            
          </div>
        )}
      </div>,

      <div key="2" className='head_control' style={{ marginTop: '13px' }}>
        {!!boardId && this.props.fields && !!this.props.fields.length && (
          <div className='autocomplete'>
            <p className='title_label fw_sb'>Enable custom fields</p>
            <Autocomplete
              wrapperProps={{ id: 'fields-autocomplete' }}
              getItemValue={item => item.name}
              items={this.props.fields}
              renderInput={props => <input placeholder="Search" {...props} />}
              renderMenu={(items, value, style) => {
                return items.length ? (
                  <div id="menu" style={{ ...style, ...this.menuStyle }} children={items} />
                ) : (
                  <div className='no_boards_found'>
                    <span>No custom fields found</span>
                  </div>
                );
              }}
              renderItem={(field, isHighlighted) => (
                <div
                  key={field.extId}
                  className='dropdown_list'
                  style={{ background: isHighlighted ? '#E6F1F7' : '' }}
                >
                  <Checkbox
                    name={field.extId}
                    id={field.extId}
                    label={field.name}
                    checked={this.fieldIsSelected(field.extId)}
                    onChange={e => this.selectField(field)}
                  />
                </div>
              )}
              value={fieldSearchVal}
              onChange={(ev, value) => this.setState({ fieldSearchVal: value })}
              onSelect={(value, field) => this.selectField(field)}
              shouldItemRender={(field, value) => {
                return field.name.toLowerCase().indexOf(value.toLowerCase()) !== -1;
              }}
              open={this.state.fieldsAutocompleteShouldOpen}
              onMenuVisibilityChange={this.handleFieldsAutocompleteVisibility}
            />
          </div>
        )}
        {!!boardId && blockedFlags && !!blockedFlags.length && (
          <div className='autocomplete'>
            { blockedFlags.length === 1 ? (
              <OverlayTrigger
                overlay={
                  <Tooltip id="blockedFlagTooltip" className="tooltip_white full_width" style={{ minWidth: 'calc((100vw - 140px) / 4 - 15px)' }}>
                    You can create a custom field of type "Boolean" in your board to use it as a blocked flag
                  </Tooltip>
                }
                delay={{ show: 10, hide: 1000 }}
                placement="top"
                trigger={['hover', 'focus', 'click']}
              >
                <p className="title_label fw_sb">
                  Select blocked flag
                  <span className="more_info" />
                </p>
              </OverlayTrigger>
            ) : (
              <p className="title_label fw_sb">Select blocked flag</p>
            )}
            <Autocomplete
              getItemValue={item => item.name}
              items={blockedFlags}
              inputProps={{ disabled: blockedFlags.length === 1 }}
              renderInput={props => <input placeholder="Search" {...props} />}
              renderMenu={(items, value, style) => {
                return items.length ? (
                  <div style={{ ...style, ...this.menuStyle }} children={items} />
                ) : (
                  <div className='no_boards_found'>
                    <span>No flags found</span>
                  </div>
                );
              }}
              renderItem={(blocked, isHighlighted) => (
                <div
                  key={blocked.extId}
                  className='dropdown_list'
                  style={{ background: isHighlighted ? '#E6F1F7' : '' }}
                >
                  <div className='dashboard_name no-img'>
                    <span className='project'>{blocked.name}</span>
                  </div>
                </div>
              )}
              value={blockedSearchVal}
              onChange={(ev, value) => this.setState({ blockedSearchVal: value })}
              onSelect={(value, blocked) => this.selectBlockedFlag(blocked)}
              shouldItemRender={(blocked, value) => {
                return blocked.name.toLowerCase().indexOf(value.toLowerCase()) !== -1;
              }}
              onMenuVisibilityChange={this.handleBlockedAutocompleteVisibility}
            />
          </div>
        )}
      </div>
    ];
  };

  render() {
    const { boardId, todo, wip, done, isUpdate } = this.state;
    const { loadingBoards, loadingLists, app, appName, dashboardModel, issue, dashboard, outside, plugin, noDashboards} = this.props;

    const shouldRenderMapping = !(isUpdate && issue);
    
    const onlyDashboard = isOnlyDashboard(app, window && window.location? window.location.search: null);
    const dashboardId = dashboard && dashboard._id || this.props.selected && this.props.selected._id? dashboard._id : dashboardId;
    const isOutside = outside && !!plugin;
    return [
      <Header key="1" title="" />,
      <React.Fragment>
        <div key="2" id="create-dashboard" className='bg_dashboard'>
          <Helmet title={dashboardModel ? 'Update dashboard' : 'Create dashboard'} />
          <div className='action_section'>
            {isOutside && !noDashboards &&!onlyDashboard ? (<Link className="btn btn-secondary btn-mw-180" to={`${dashboardId ? `/dashboard/${dashboardId}/process` : '/'}`}>
              Back
            </Link>) : (<div></div>)}
            <div className={`action_button ${(!dashboardModel && app === 'jira' && !!boardId) && 'inline-checkbox'}`}>
              {!dashboardModel && app === 'jira' && !!boardId && (
                <Switch
                  id="anonymize"
                  className="switch_box_right_label"
                  name="anonymize"
                  label="Don’t store task names"
                  disabled={this.state.board.anonymize || !(todo.length && wip.length && done.length)}
                  checked={this.state.anonymize || this.state.board.anonymize}
                  onChange={e => this.setState({ anonymize: e.checked })}
                />
              )}
              
              {dashboardModel ? (
                [
                  hasDashboardAccess(this.props.user, dashboardModel._id, 'edit') ? (
                    <Switch
                      id="wipeOut"
                      className="switch_box_right_label"
                      key="4"
                      name="wipeOut"
                      label="Wipe out dashboard data"
                      checked={this.state.wipeOut}
                      onChange={this.handleCheckboxChange}
                      disabled={!(todo.length && wip.length && done.length)}
                    />
                  ) : (
                    undefined
                  ),
                  <Confirm
                    key="3"
                    disabled={!this.state.wipeOut}
                    onConfirm={ev => this.handleUpdate()}
                    body={
                      <p>
                        We'll remove all your dashboard historical 
                        records and we'll sync your data again. We'll preserve the configuration of your
                        dashboard.
                      </p>
                    }
                    confirmText="Confirm"
                    icon={deleteIcon}
                    title="Wipe out dashboard data"
                  >
                    <Button
                      className='btn-mw-200'
                      disabled={!(todo.length && wip.length && done.length)}
                      onClick={this.handleUpdate}
                    >
                      <img src={saveIcon} alt="" />
                      {this.props.saving ? 'Loading...' : 'Update dashboard'}
                    </Button>
                  </Confirm>
                ]
              ) : (
                <Button
                  className='btn-mw-200'
                  disabled={!(todo.length && wip.length && done.length)}
                  onClick={this.handleCreate}
                >
                  <img src={addIcon} alt="" />
                  {this.props.saving ? 'Loading...' : 'Create dashboard'}
                </Button>
              )}
              
            </div>
          </div>

          {app !== 'azure' ? this.renderDefaultDropdowns() : this.renderAzureDropdowns()}

          {shouldRenderMapping && !!boardId && (
            <div className='lists_section'>
              <p className='title_label fw_sb'>
                Map <FormattedMessage id={`${app}.list`} prefix={appName} mode="capitalize" plural="true" />
              </p>
              <div className="list_arrow">
                <img src={spinerArrow} alt="#" />
              </div>
              <div className='lists_section_scroll'>
                {this.renderLists()}
              </div>
            </div>
          )}
        </div>
        {(loadingBoards || loadingLists) && <div className='table-loading'><Loading /></div>}
      </React.Fragment>
    ];
  }
}

const mapDispatchToProps = dispatch => {
  return {
    setError: err => dispatch(handleError(err)),
    setDataLoading: val => dispatch(appDataIsLoading(val)),
    loadingDone: () => dispatch(isLoading(false)),
    loadingStart: () => dispatch(isLoading(true)),
    listAll: () => dispatch(listAll()),
    getTeams: () => dispatch(getTeams()),
    getBoards: (value, pid, tid, projectsSkip, boardsSkip) =>
      dispatch(getBoards(value, pid, tid, projectsSkip, boardsSkip)),
    getLists: (boardId, orgId, dashboardId) => dispatch(getLists(boardId, orgId, dashboardId)),
    getAreas: pid => dispatch(getAreas(pid)),
    create: model => dispatch(create(model)),
    update: (dashboardId, model) => dispatch(update(dashboardId, model)),
    updateDashboardName: (dashboardId, name) => dispatch(updateDashboardName(dashboardId, name)),
    updateModel: dashboard => dispatch(updateModel(dashboard)),
    resetSelectedDashboard: () => dispatch(selectDashboard(undefined)),
    resetModel: () => {
      dispatch(updateModel(undefined));
    }
  };
};

const mapStateToProps = state => {
  return {
    app: state.app.app,
    appName: state.app.appName,
    outside: state.app.outside,
    plugin: state.app.plugin,
    user: state.auth.user,
    dashboards: state.dashboards.list,
    boards: state.dashboards.boards,
    boardsInfo: state.dashboards.boardsInfo,
    loadingBoards: state.dashboards.loadingBoards,
    lists: state.dashboards.lists,
    mapping: state.dashboards.mapping,
    fields: state.dashboards.fields,
    teams: state.dashboards.teams,
    areas: state.dashboards.areas,
    loadingLists: state.dashboards.loadingLists,
    created: state.dashboards.newCreated,
    dashboardModel: state.dashboards.dashboardModel,
    issue: state.dashboards.issue,
    saving: state.dashboards.isLoading,
    noDashboards: state.dashboards.noDashboards
  };
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(CreateIntegrationDashboard));
